import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React from "react";
import { Controller } from "react-hook-form";
import LogoUlo from "../../../components/LogoUlo/LogoUlo";
import { ResetPasswordFormModel } from "../hooks/useResetPasswordForm";

interface ResetPasswordCardProps {
    resetPasswordForm: ResetPasswordFormModel;
}

const ResetPasswordCard = ({ resetPasswordForm }: ResetPasswordCardProps) => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isLaptopScreen = useMediaQuery(theme.breakpoints.up("md"));
    const {
        form,
        isButtonLoading,
        onSubmit,
        showPasswordBaru,
        showPasswordBaruUlang,
        toggleShowPasswordBaru,
        toggleShowPasswordBaruUlang,
    } = resetPasswordForm;

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = form;

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100vh" }}
        >
            <Box
                minHeight="50vh"
                minWidth="50vw"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="fit-content"
                paddingX={isTabletScreen ? 4 : undefined}
                paddingY={4}
                border="1px solid rgba(0, 0, 0, 0.12)"
                borderRadius="6px"
                sx={{ backgroundColor: "#fff" }}
            >
                <Stack spacing={3} alignItems="center">
                    <Link
                        href="https://ukmlokal.com/"
                        rel="noreferrer"
                        target="_blank"
                    >
                        <LogoUlo width="100px" />
                    </Link>
                    <Box>
                        <Typography
                            component="div"
                            textAlign="center"
                            variant="h6"
                            fontWeight="bold"
                        >
                            Atur Ulang{" "}
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#20965F" }}
                                display="inline"
                            >
                                Kata Sandi
                            </Typography>
                        </Typography>
                        <Typography
                            marginTop={1}
                            variant="body2"
                            textAlign="center"
                            width="300px"
                        >
                            Masukkan kata sandi baru anda.
                        </Typography>
                    </Box>
                    <form
                        style={{
                            width: "100%",
                            maxWidth: "600px",
                            minWidth: "300px",
                        }}
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Typography variant="body2">Password</Typography>
                                <Controller
                                    name="passwordBaru"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.passwordBaru)}
                                        >
                                            <OutlinedInput
                                                id="passwordBaru"
                                                type={
                                                    showPasswordBaru
                                                        ? "text"
                                                        : "password"
                                                }
                                                autoComplete="new-password"
                                                autoFocus={isLaptopScreen}
                                                placeholder="Minimal 8 karakter"
                                                size="small"
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={
                                                                toggleShowPasswordBaru
                                                            }
                                                            edge="end"
                                                        >
                                                            {showPasswordBaru ? (
                                                                <VisibilityOff />
                                                            ) : (
                                                                <Visibility />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                {...field}
                                            />
                                            <FormHelperText>
                                                {errors.passwordBaru
                                                    ? errors.passwordBaru
                                                          .message
                                                    : " "}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    Ulang Password
                                </Typography>
                                <Controller
                                    name="passwordBaruUlang"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(
                                                errors.passwordBaruUlang,
                                            )}
                                        >
                                            <OutlinedInput
                                                id="passwordBaruUlang"
                                                type={
                                                    showPasswordBaruUlang
                                                        ? "text"
                                                        : "password"
                                                }
                                                autoComplete="new-password"
                                                autoFocus={isLaptopScreen}
                                                placeholder="Minimal 8 karakter"
                                                size="small"
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={
                                                                toggleShowPasswordBaruUlang
                                                            }
                                                            edge="end"
                                                        >
                                                            {showPasswordBaruUlang ? (
                                                                <VisibilityOff />
                                                            ) : (
                                                                <Visibility />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                {...field}
                                            />
                                            <FormHelperText>
                                                {errors.passwordBaruUlang
                                                    ? errors.passwordBaruUlang
                                                          .message
                                                    : " "}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <LoadingButton
                            loading={isButtonLoading}
                            loadingPosition="center"
                            type="submit"
                            fullWidth
                            size="medium"
                            variant="contained"
                            sx={{ marginTop: 3 }}
                        >
                            Ganti kata sandi
                        </LoadingButton>
                    </form>
                </Stack>
            </Box>
        </Box>
    );
};

export default ResetPasswordCard;
